import type { GameCard } from "../shared/deck";
import type { CardBack, CardCollection } from "../shared/collections";
import { createCard } from "./card";
import { createCardBack } from "./card-back";
import { t } from "./i18n";

/** A single large card, independent of the A4 sheet scale. */
export function createCardPreview(dialog: HTMLDialogElement, onChange: (index: number, side: "front" | "back") => void) {
  const stage = dialog.querySelector<HTMLElement>(".card-preview-stage")!;
  const previous = dialog.querySelector<HTMLButtonElement>("[data-card-previous]")!;
  const next = dialog.querySelector<HTMLButtonElement>("[data-card-next]")!;
  const counter = dialog.querySelector<HTMLElement>(".card-preview-counter")!;
  const details = dialog.querySelector<HTMLElement>(".card-preview-details")!;
  const heading = dialog.querySelector<HTMLElement>(".card-preview-heading")!;
  const sides = document.createElement("div");
  sides.className = "card-preview-sides";
  sides.setAttribute("role", "group");
  const front = document.createElement("button");
  const reverse = document.createElement("button");
  front.type = reverse.type = "button";
  sides.append(front, reverse);
  heading.insertBefore(sides, heading.lastElementChild);
  let cards: readonly GameCard[] = [];
  let collection: CardCollection | null = null;
  let back: CardBack | undefined;
  let side: "front" | "back" = "front";
  let index = 0;
  let diameter = 85;
  let start: { x: number; y: number; id: number } | undefined;

  function render(direction = 0) {
    stage.replaceChildren(side === "front" ? createCard(cards[index], { collection }) : createCardBack(back));
    sides.setAttribute("aria-label", t("Face de la carte"));
    front.textContent = t("Recto");
    reverse.textContent = t("Verso");
    front.setAttribute("aria-pressed", String(side === "front"));
    reverse.setAttribute("aria-pressed", String(side === "back"));
    counter.textContent = `${index + 1} / ${cards.length}`;
    counter.setAttribute("aria-label", t("Carte {current} sur {total}", { current: index + 1, total: cards.length }));
    details.textContent = `Ø ${diameter} mm · ${side === "front" ? t("{count} symboles", { count: cards[index].symbols.length }) : t("Dos de carte")}`;
    previous.disabled = index === 0;
    next.disabled = index === cards.length - 1;
    if (document.activeElement === previous && previous.disabled) next.focus();
    else if (document.activeElement === next && next.disabled) previous.focus();
    if (direction && !matchMedia("(prefers-reduced-motion: reduce)").matches)
      stage.firstElementChild!.animate([
        { opacity: 0.35, transform: `translateX(${direction * 22}px)` },
        { opacity: 1, transform: "translateX(0)" },
      ], { duration: 160, easing: "ease-out" });
    onChange(index, side);
  }
  front.addEventListener("click", () => {
    if (!cards.length || side === "front") return;
    side = "front";
    render();
  });
  reverse.addEventListener("click", () => {
    if (!cards.length || side === "back") return;
    side = "back";
    render();
  });
  function move(delta: number) {
    const target = Math.max(0, Math.min(cards.length - 1, index + delta));
    if (target === index) return;
    index = target;
    render(Math.sign(delta));
  }
  previous.addEventListener("click", () => move(-1));
  next.addEventListener("click", () => move(1));
  dialog.addEventListener("keydown", event => {
    if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
      event.preventDefault();
      move(event.key === "ArrowLeft" ? -1 : 1);
    }
  });
  stage.addEventListener("pointerdown", event => {
    if (!event.isPrimary || event.button !== 0) { start = undefined; return; }
    start = { x: event.clientX, y: event.clientY, id: event.pointerId };
    stage.setPointerCapture(event.pointerId);
  });
  stage.addEventListener("pointercancel", () => { start = undefined; });
  stage.addEventListener("pointerup", event => {
    const gesture = start;
    start = undefined;
    if (!gesture || gesture.id !== event.pointerId) return;
    const dx = event.clientX - gesture.x, dy = event.clientY - gesture.y;
    if (Math.abs(dx) >= 45 && Math.abs(dx) > Math.abs(dy) * 1.3) move(dx < 0 ? 1 : -1);
  });
  dialog.addEventListener("close", () => { start = undefined; });

  return {
    open(deck: readonly GameCard[], selected: number, diameterMm: number, selectedCollection?: CardCollection | null, selectedBack?: CardBack, initialSide: "front" | "back" = "front") {
      if (!deck.length) return;
      cards = deck;
      index = Math.max(0, Math.min(deck.length - 1, selected));
      diameter = diameterMm;
      collection = selectedCollection ?? null;
      back = selectedBack ?? collection?.back;
      side = initialSide;
      render();
      if (!dialog.open) dialog.showModal();
    },
  };
}
